<script>
import { mapGetters, mapActions } from 'vuex'
import productMixin from '@/mixins/edit-related-products.mixin.js'
import ProductsEdit from '@/components/related-products-edit'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'ProviderEdit',
  components: {
    ProductsEdit,
  },
  mixins: [productMixin],
  props: {
    create: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      progress: false,
      form: {
        active: true,
        name: '',
        description: '',
        twoFactorEnforced: false,
        timezone: 'Australia/Melbourne',
        externalProviders: [],
        products: [],
      },
    }
  },
  computed: {
    ...mapGetters('products', ['products']),
    rules() {
      const rules = {
        name: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        timezone: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'change',
          },
        ],
        status: [
          {
            required: false,
            message: 'This field is required',
            trigger: 'change',
          },
        ],
        products: [
          {
            type: 'array',
            required: true,
            message: 'At least one selected product is required',
            trigger: 'change',
          },
          {
            validator: (rule, value, callback) => {
              const versionNotSelected = value.find(item => {
                return !item.versionId && item.latest
              })
              return callback(versionNotSelected && new Error('Product version is required'))
            },
            trigger: ['blur', 'change'],
          },
        ],
      }
      return rules
    },
    providerId() {
      return this.$route.params.id || null
    },
    title() {
      return this.providerId ? 'Edit Provider' : 'Create Provider'
    },
    back() {
      if (this.providerId) {
        return {
          name: 'provider-details',
          params: {
            id: this.providerId,
            edit: null,
          },
        }
      } else {
        return {
          name: 'providers',
        }
      }
    },
  },
  async created() {
    if (this.providerId) {
      const [, response] = await this.getProvider({ id: this.providerId })
      this.prefillFields(response)
    }
    this.loading = false
  },
  methods: {
    ...mapActions('provider', [
      'getProvider',
      'createProvider',
      'updateProvider',
    ]),
    prefillFields(provider) {
      if (provider) {
        const activeProducts = provider.products.filter(p => p.product.active)
        const formProducts = activeProducts.map(p => {
          const { version } = p
          let versionId = null
          let latest = false

          if (version) {
            versionId = version.id
            latest = version.latest
          } else if (p.latest) {
            versionId = 'latest'
            latest = true
          }

          return {
            productId: p.product.id,
            versionId: versionId,
            latest,
          }
        })

        this.form = {
          ...provider,
          products: formProducts,
        }
      }
    },
    onSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          return false
        }

        this.progress = true
        const form = cloneDeep(this.form)
        form.products.forEach(pr => {
          if (pr.versionId === 'latest' || pr.latest === true) delete pr.versionId
        })
        const action = this.providerId ? 'updateProvider' : 'createProvider'
        const response = await this[action](form)
        const [error, result] = response

        this.$notifier({
          response,
          successMsg: `Successfully ${this.isCreate ? 'created' : 'updated'}`,
        })

        if (!error) {
          this.$router.push({
            name: 'provider-details',
            params: {
              id: result.id,
            },
          })
        }

        this.progress = false

        // this.$emit('submit', this.form)
      })
    },
    onCancel() {
      this.$router.push(this.back)
    },
  },
}
</script>

<template>
  <main-layout
    :title="title"
    :back="back"
    :loading="loading"
  >
    <el-card>
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-position="top"
      >
        <div :class="$style.inputs">
          <el-form-item
            label="Provider Name"
            prop="name"
          >
            <el-input
              v-model="form.name"
              placeholder="Name"
            />
          </el-form-item>
          <el-form-item
            label="Provider Description"
            prop="description"
            type="textarea"
          >
            <el-input
              v-model="form.description"
              placeholder="Description"
            />
          </el-form-item>
          <el-form-item
            label="Provider Timezone"
            prop="timezone"
          >
            <el-select
              v-model="form.timezone"
              placeholder="please select timezone"
            >
              <el-option
                label="Australia/Melbourne"
                value="Australia/Melbourne"
              />
            </el-select>
          </el-form-item>
          <!-- <el-form-item
          prop="twoFactorEnforced"
        >
          <el-checkbox
            v-model="form.twoFactorEnforced"
            label="Enforce Two-Factor Authentication"
          />
        </el-form-item> -->
          <el-form-item
            label="Status"
            prop="status"
          >
            <el-select
              v-model="form.active"
              placeholder="Please select status"
            >
              <el-option
                label="Active"
                :value="true"
              />
              <el-option
                label="Inactive"
                :value="false"
              />
            </el-select>
          </el-form-item>
        </div>

        <products-edit
          :products="activeProducts"
          :form-products="form.products"
          :external-providers="form.externalProviders"
          @check-product="checkProduct"
          @select-version="selectVersion"
          @change-provider-id="enterProviderId"
        />

        <div :class="$style.formFooter">
          <el-button
            type="primary"
            plain
            @click="onCancel"
          >
            Cancel
          </el-button>
          <el-button
            type="primary"
            :loading="progress"
            @click="onSubmit"
          >
            Save
          </el-button>
        </div>
      </el-form>
    </el-card>
  </main-layout>
</template>

<style lang="scss" module>
.inputs {
  max-width: rem(470px);
}

.products {
  display: flex;
  flex-direction: column;

  :global {
    .el-form-item__content {
      flex-direction: column;
    }
  }
  &:global(.is-error .el-checkbox__inner) {
    border-color: var(--color-error);
  }
  &:global(.is-error > .el-form-item__content) {
    margin-bottom: rem(24px);
  }
}

.product {
  display: flex;
  align-items: center;
  width: 100%;
  margin: rem(0 0 20px);
}

.productCheckbox {
  width: rem(200px);
}
</style>
